import { z } from 'zod';
import { blobAssetSchema, readGrantSchema, sourceImageReadGrantSchema, SOURCE_IMAGE_MAX_BYTES } from '../macroCalibration/jobs';
import { calibrationEvidenceSourceImageSchema } from '../macroCalibration/evidencePages';
import { sha256HexSchema } from './canonicalJson';

/** Binary closure bytes travel in bounded downloads; inline data never grows with full-source inventory. */
export const CALIBRATION_ATTACHMENT_INLINE_MAX_BYTES = 1024 * 1024;
export const CALIBRATION_PAGE_FILE = /^(refs\/page-\d{1,3}\.bin|manifest\/page-\d{1,3}\.json)$/;
const base64 = (limit: number) => z.string().max(Math.ceil(limit / 3) * 4).regex(/^[A-Za-z0-9+/]*={0,2}$/);
const samePin = (value: { asset?: { objectKey?: string; digest?: string; byteCount?: number; contentType?: string };
  readGrant?: { objectKey?: string; digest?: string; byteCount?: number; contentType?: string } }) =>
  value.asset !== undefined && value.readGrant !== undefined && typeof value.asset.digest === 'string'
  && value.asset.objectKey === value.readGrant.objectKey && value.asset.digest === value.readGrant.digest
  && value.asset.byteCount === value.readGrant.byteCount && value.asset.contentType === value.readGrant.contentType;
export const calibrationAttachmentReadAssetSchema = z.union([
  z.object({ asset: blobAssetSchema, readGrant: readGrantSchema }).strict().refine(samePin, 'Grant must preserve the exact asset pin'),
  z.object({ asset: calibrationEvidenceSourceImageSchema, readGrant: sourceImageReadGrantSchema }).strict().refine(samePin, 'Grant must preserve the exact source pin'),
]);
export const calibrationAttachmentAssetSchema = z.union([
  z.object({ asset: blobAssetSchema, bytesBase64: base64(26_214_400) }).strict(),
  z.object({ asset: calibrationEvidenceSourceImageSchema, bytesBase64: base64(SOURCE_IMAGE_MAX_BYTES) }).strict(),
  calibrationAttachmentReadAssetSchema,
]);
export const calibrationAttachmentReadPageSchema = z.object({ file: z.string().regex(CALIBRATION_PAGE_FILE),
  sha256: sha256HexSchema, asset: blobAssetSchema, readGrant: readGrantSchema }).strict().refine(value =>
    samePin(value) && value.sha256 === value.asset?.digest, 'Page grant must preserve its exact pin');
export const calibrationAttachmentPageSchema = z.union([
  z.object({ file: z.string().regex(CALIBRATION_PAGE_FILE), sha256: sha256HexSchema, bytesBase64: base64(26_214_400) }).strict(),
  calibrationAttachmentReadPageSchema,
]);
export type CalibrationAttachmentAsset = z.infer<typeof calibrationAttachmentAssetSchema>;
export type CalibrationPageBody = z.infer<typeof calibrationAttachmentPageSchema>;
